import {throwError as observableThrowError, of as observableOf, Observable, throwError} from 'rxjs';
import {Injectable} from '@angular/core';
import {Session} from './Session';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {TaxplanIQServices} from "./TaxplanIQServices";
import {PATH, SOURCE_TYPE} from "../constants/Tpiq.constants";

export function getTrackersTableDatatUrl(userId, org, trackerId) {
    return `/TaxPlanningServices/users/${userId}/orgs/${org}/tracker/${trackerId}/list`;
}

export function getTrackersListUrl(org) {
    return `/TaxPlanningServices/orgs/${org}/tracker/list`;
}

export function getTrackersCountUrl(userId, org) {
    return `/TaxPlanningServices/users/${userId}/orgs/${org}/tracker/count`;
}

export function getBudgestUrl(org, userId, taskId) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/tasks/${taskId}/budgets`;
}

export function getResendEngagementEmailUrl(id, userId) {
    return `/TaxPlanningServices/engagements/${id}/resendemail?userId=${userId}`;
}

export function getUpdateEngSettingsUrl(orgId, userId, engagementId) {
    return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/engagements/${engagementId}/settings`;
}

export function getEngagementHistoryUrl(org, userId, engId) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/source/${engId}/history`;
}

export function getChecklistItemsInSearchUrl(org, workflowId) {
    return `/TaxPlanningServices/orgs/${org}/workflows/${workflowId}/checklist`;
}

export function getTaskMetaData(org, userId, taskId) {
    return `/TaxPlanningServices/org/${org}/users/${userId}/tasks/${taskId}/metadata`;
}

export function getCreateTodoUrl(org, userId, taskId) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/tasks/${taskId}/todo`;
}

declare const _: any;

@Injectable({
    providedIn: 'root'
})
export class TrackerService extends TaxplanIQServices {
    trackersList = {};

    constructor(http: HttpClient) {
        super(http);
    }

    updateTaskForTracker(data: any, taskId): Observable<any> {
        const url = this.interpolateUrl(PATH.SINGLE_TASK_TRACKER_SERVICE, null, {
            userId: Session.getUser().id,
            orgId: Session.getOrg(),
            taskId
        });
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackersTableData(data: any, trackerId): Observable<any> {
        const url = getTrackersTableDatatUrl(Session.getUser().id, Session.getOrg(), trackerId);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackerStatuses(data: any): Observable<any> {
        let url = this.interpolateUrl(PATH.TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId: data.id
        });
        url = url + '/count';
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackerSubStatuses(id): Observable<any> {
        let url = this.interpolateUrl(PATH.SUB_STATUS_SERVICES, null, {orgId: Session.getOrg()});
        url = url + '/preferences/type/' + id;
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateInfo(data: any, type, trackerId): Observable<any> {
        const url = this.interpolateUrl(PATH.TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId
        });
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackers(clearCache?): Observable<any> {
        if (clearCache) {
            this.trackersList = {};
        }
        if (this.trackersList && !_.isEmpty(this.trackersList)) {
            return observableOf(this.trackersList);
        }
        const url = getTrackersListUrl(Session.getOrg());
        return this.query(url, SOURCE_TYPE.JAVA).pipe(map((res) => {
            this.trackersList = res;
            return this.trackersList;
        }), catchError(this.handleError));
    }

    getTrackerInfo(trackerId): Observable<any> {
        const url = this.interpolateUrl(PATH.TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId
        });
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTasksByUser(trackerId): Observable<any> {
        let url = this.interpolateUrl(PATH.TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId
        });
        url = url + '/resourcebystatus/count?org=' + Session.getOrg();
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackerStatus(type): Observable<any> {
        let url = this.interpolateUrl(PATH.SUB_STATUS_SERVICES, null, {orgId: Session.getOrg()});
        url = url + '/preferences/taskTypes/' + type;
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackerOptions(trackerId): Observable<any> {
        let url = this.interpolateUrl(PATH.TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId
        });
        url = url + '/recurringfilter';
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTaskTracker(taskId) {
        const url = this.interpolateUrl(PATH.SINGLE_TASK_TRACKER_SERVICE, null, {
            userId: Session.getUser().id,
            orgId: Session.getOrg(),
            taskId
        });
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateTaskStatusOrder(data: any, taskId): Observable<any> {
        const url = this.interpolateUrl(PATH.ROLL_TRACKER_STATUS_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            taskId
        });
        return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getAllTrackerData(data: any, trackerId): Observable<any> {
        const rawUrl = this.interpolateUrl(PATH.ALL_TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId
        });
        return this.create(rawUrl, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    addSinglePageTodo(org, user, taskId, data): Observable<any> {
        const url = getCreateTodoUrl(org, user, taskId);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getUploadUrl(data: any, trackerId): string {
        const url = getTrackersTableDatatUrl(Session.getUser().id, Session.getOrg(), trackerId);
        return url;
    }

    getTasksByUserWithFilters(data, trackerId): Observable<any> {
        let url = this.interpolateUrl(PATH.TASK_TRACKER_SERVICE, null, {
            id: Session.getUser().id,
            orgId: Session.getOrg(),
            trackerId
        });
        url = url + '/resourcebystatus/count?org=' + Session.getOrg();
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTrackersCount(org, userId): Observable<any> {
        const url = getTrackersCountUrl(userId, org);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getBudgets(org, userId, taskId): Observable<any> {
        const url = getBudgestUrl(org, userId, taskId);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    resendEngagementEmail(id, data): Observable<any> {
        const url = getResendEngagementEmailUrl(id, Session.getUser().id);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateEngSettings(orgId, userId, engId, data): Observable<any> {
        const url = getUpdateEngSettingsUrl(orgId, userId, engId);
        return this.create(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getEngHistory(orgId, userId, engId): Observable<any> {
        const url = getEngagementHistoryUrl(orgId, userId, engId);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getChecklistDataInSearch(orgId, workflowId): Observable<any> {
        const url = getChecklistItemsInSearchUrl(orgId, workflowId);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTaskPageMetaData(taskId) {
        const url = getTaskMetaData(Session.getOrg(), Session.getUser().id, taskId);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getUsers(org?): Observable<any> {
      const url = this.interpolateUrl(PATH.ASSIGNEES_SERVICE, {}, {id: Session.getUser().id, org: Session.getOrg()});
      return this.create(url, {"fromIndex": -1}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    private handleError(error: HttpErrorResponse) {
        return throwError(error.error);
    }
}
